
import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d6d88g2tehdqnvnpipdg';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('service', formData.service);
      body.append('message', formData.message);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-4 font-semibold">
              CONTACT US
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-navy mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Need help with business funding, personal funding, or tradelines? Our team is here to guide
              you through the process and answer your questions.
            </p>
          </div>
        </section>

        {/* Info Cards */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Office Location */}
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-navy/10 rounded-full mx-auto mb-5">
                  <i className="ri-map-pin-line text-2xl text-navy"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Our Office</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  DN Group, LLC<br />
                  1302 Waugh Dr #413<br />
                  Houston, Texas 77019
                </p>
              </div>

              {/* Phone */}
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-navy/10 rounded-full mx-auto mb-5">
                  <i className="ri-phone-line text-2xl text-navy"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Main Line</h3>
                <a
                  href="tel:+13466169099"
                  className="text-lg font-semibold text-navy hover:text-navy-light transition-colors cursor-pointer"
                >
                  (346) 616-9099
                </a>
                <p className="text-sm text-gray-500 mt-2">Call or text us anytime</p>
              </div>

              {/* Office Hours */}
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-navy/10 rounded-full mx-auto mb-5">
                  <i className="ri-time-line text-2xl text-navy"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Office Hours</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong className="text-navy">Monday – Friday</strong>
                  <br />
                  9:00 AM – 6:00 PM (Central Time)
                </p>
                <p className="text-sm text-gray-500 mt-3">Saturday – Sunday: Closed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy mb-4">Send Us a Message</h2>
              <p className="text-base text-gray-600">
                Fill out the form below and we'll get back to you within one business day.
              </p>
            </div>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl p-8 md:p-10 shadow-sm border border-gray-100"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(123) 456-7890"
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Business Funding">Business Funding</option>
                    <option value="Personal Funding">Personal Funding</option>
                    <option value="Buy Tradelines">Buy Tradelines</option>
                    <option value="Business Credit Building">Business Credit Building</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Tell us about your funding needs..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {formData.message.length}/500
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-navy text-white py-3.5 rounded-lg text-sm font-medium hover:bg-navy-light transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                  <p className="text-sm text-green-700 font-medium">
                    <i className="ri-check-line mr-1"></i>
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-center">
                  <p className="text-sm text-red-700 font-medium">
                    <i className="ri-error-warning-line mr-1"></i>
                    Something went wrong. Please try again or call us directly.
                  </p>
                </div>
              )}

              <p className="text-xs text-gray-400 mt-4 text-center">
                <i className="ri-lock-line mr-1"></i>
                Your information is secure and will never be shared.
              </p>
            </form>
          </div>
        </section>

        {/* Map */}
        <section className="w-full">
          <iframe
            title="DN Group LLC Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.0!2d-95.3985!3d29.7575!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640bf9a0b0b0b0b%3A0x0!2s1302+Waugh+Dr+%23413%2C+Houston%2C+TX+77019!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full"
          />
        </section>

        {/* CTA */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-navy mb-6">Ready to Get Started?</h2>
            <p className="text-lg text-gray-600 mb-10">
              Apply now and let our team build a funding strategy tailored to your goals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/apply"
                className="inline-flex items-center bg-navy text-white px-8 py-4 rounded-lg text-base font-medium hover:bg-navy-light transition-colors whitespace-nowrap cursor-pointer"
              >
                Start Your Application
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <a
                href="tel:+13466169099"
                className="inline-flex items-center border-2 border-navy text-navy px-8 py-4 rounded-lg text-base font-medium hover:bg-navy hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (346) 616-9099
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
